import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { campaignApi } from '../services/api';

export default function CampaignDetails() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const queryClient = useQueryClient();
  const [downloadingExport, setDownloadingExport] = useState<string | null>(null);

  if (!campaignId) {
    return <div className="error">Campaign ID is required</div>;
  }

  const { data: campaign, isLoading, error } = useQuery({
    queryKey: ['campaign-details', campaignId],
    queryFn: () => campaignApi.getCampaignDetails(campaignId),
  });

  const updateStatusMutation = useMutation({
    mutationFn: (status: string) => campaignApi.updateCampaignStatus(campaignId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-details', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  const handleStatusUpdate = (status: string) => {
    if (confirm(`Are you sure you want to update the status to "${status}"?`)) {
      updateStatusMutation.mutate(status);
    }
  };

  const handleExportDownload = async (fileType: 'csv' | 'json') => {
    try {
      setDownloadingExport(fileType);
      const downloadUrl = await campaignApi.getExportUrl(campaignId, fileType);
      
      // Create a temporary link to trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `campaign-${campaignId}-export.${fileType}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      alert(`Failed to download ${fileType.toUpperCase()} export: ${error}`);
    } finally {
      setDownloadingExport(null);
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  };

  const getStatusBadge = (status: string) => {
    const statusClass = `campaign-status status-${status}`;
    return <span className={statusClass}>{status.toUpperCase()}</span>;
  };

  if (isLoading) {
    return <div className="loading">Loading campaign details...</div>;
  }

  if (error) {
    return (
      <div className="error">
        Error loading campaign: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    );
  }

  if (!campaign) {
    return <div className="error">Campaign not found</div>;
  }

  return (
    <div>
      <div className="admin-header">
        <div>
          <Link to="/campaigns" className="btn btn-secondary">← Back to Campaigns</Link>
          <h1 style={{ margin: '0.5rem 0' }}>{campaign.name}</h1>
          <div>{getStatusBadge(campaign.status)}</div>
        </div>
        <div>
          {campaign.status === 'submitted' && (
            <button
              onClick={() => handleStatusUpdate('complete')}
              className="btn btn-success"
              disabled={updateStatusMutation.isPending}
            >
              {updateStatusMutation.isPending ? 'Updating...' : 'Mark Complete'}
            </button>
          )}
        </div>
      </div>

      <div className="campaign-details">
        <div className="detail-section">
          <h3>Campaign Information</h3>
          <ul className="detail-list">
            <li>
              <span>Campaign ID:</span>
              <span>{campaign.campaignId}</span>
            </li>
            <li>
              <span>Card Size:</span>
              <span>{campaign.cardSize}</span>
            </li>
            <li>
              <span>Paper Stock:</span>
              <span>{campaign.paperStock}</span>
            </li>
            <li>
              <span>Created:</span>
              <span>{formatDate(campaign.createdAt)}</span>
            </li>
            <li>
              <span>Last Updated:</span>
              <span>{formatDate(campaign.updatedAt)}</span>
            </li>
          </ul>
        </div>

        <div className="detail-section">
          <h3>Recipients ({campaign.recipients.length})</h3>
          {campaign.recipients.length > 0 ? (
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {campaign.recipients.slice(0, 10).map((recipient) => (
                <div key={recipient.recipientId} style={{ 
                  padding: '0.5rem', 
                  borderBottom: '1px solid #eee',
                  fontSize: '0.875rem'
                }}>
                  <div style={{ fontWeight: 'bold' }}>
                    {recipient.firstName} {recipient.lastName}
                  </div>
                  <div style={{ color: '#666' }}>
                    {recipient.address1}, {recipient.city}, {recipient.state} {recipient.postalCode}
                  </div>
                </div>
              ))}
              {campaign.recipients.length > 10 && (
                <div style={{ padding: '0.5rem', fontStyle: 'italic', color: '#666' }}>
                  ... and {campaign.recipients.length - 10} more recipients
                </div>
              )}
            </div>
          ) : (
            <div style={{ color: '#666', fontStyle: 'italic' }}>No recipients uploaded</div>
          )}
        </div>

        <div className="detail-section">
          <h3>Message Template</h3>
          <div style={{ 
            backgroundColor: 'white', 
            padding: '1rem', 
            borderRadius: '4px',
            border: '1px solid #ddd',
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            whiteSpace: 'pre-wrap'
          }}>
            {campaign.messageTemplate || 'No message template set'}
          </div>
        </div>

        <div className="detail-section">
          <h3>Order Information</h3>
          {campaign.order ? (
            <ul className="detail-list">
              <li>
                <span>Order ID:</span>
                <span>{campaign.order.orderId}</span>
              </li>
              <li>
                <span>Vendor:</span>
                <span>{campaign.order.vendorName}</span>
              </li>
              <li>
                <span>Status:</span>
                <span>{getStatusBadge(campaign.order.status)}</span>
              </li>
              <li>
                <span>Submitted:</span>
                <span>{formatDate(campaign.order.submittedAt)}</span>
              </li>
              <li>
                <span>Recipient Count:</span>
                <span>{campaign.order.recipientCount}</span>
              </li>
            </ul>
          ) : (
            <div style={{ color: '#666', fontStyle: 'italic' }}>No order submitted yet</div>
          )}
        </div>

        {campaign.order && (
          <div className="detail-section">
            <h3>Export Files</h3>
            <div className="export-links">
              <button
                onClick={() => handleExportDownload('csv')}
                className="btn btn-primary"
                disabled={downloadingExport === 'csv'}
              >
                {downloadingExport === 'csv' ? 'Downloading...' : 'Download CSV'}
              </button>
              <button
                onClick={() => handleExportDownload('json')}
                className="btn btn-primary"
                disabled={downloadingExport === 'json'}
              >
                {downloadingExport === 'json' ? 'Downloading...' : 'Download JSON'}
              </button>
            </div>
            <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#666' }}>
              Export files contain recipient data and asset URLs for vendor processing.
            </div>
          </div>
        )}

        {campaign.designUrl && (
          <div className="detail-section">
            <h3>Design Preview</h3>
            <div style={{ textAlign: 'center' }}>
              <img 
                src={campaign.designUrl} 
                alt="Campaign design"
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '300px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}